import { EPublisherSolutionModel } from 'common/contracts';
import { EPermissionGroup } from 'common/permissions.types';
import { ELocalStorageKeys } from 'constants/enums';
import { EAnalyticsState, EOrdersState } from 'constants/navigation';
import { localStorageUtil } from 'utils/localStorageUtil';
import { EPlayersState } from 'views/Players/PlayersView';
import { EPricingFormState } from 'views/Pricing/PricingView';
import { EReportsTabs } from 'views/Reports/types';

import { ESettingsState } from 'components/Settings/Settings.types';
import { EStoreState } from 'components/Theme/Theme';

import { EFeatureFlag } from '../../constants/enums';

import {
  EDashboardPageTitle,
  EDashboardRoute,
  EManagementPageTitle,
  EManagementRoute,
  EOffersPageTitle,
  EOffersRoute,
  EPortalPageTitle,
  EPortalRoute,
  ESdkPageTitle,
  ESdkRoute,
  ESharedPageTitle,
  EStudioPageTitle,
  EStudioRoute,
  PageTitle
} from './pages';
import {
  ESideBarChipConfig,
  ESideBarChipType,
  ESidebarGroupKey,
  SideBarGroup,
  SideBarItem
} from './types';
import { ESideBarContext } from './types';

const isCheckoutUser =
  localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
  EPublisherSolutionModel.CHECKOUT;
// =====================
// Individual page definitions
// =====================

// Company-level pages
export const Orders: SideBarItem = {
  title: EDashboardPageTitle.ORDERS,
  url: EDashboardRoute.ORDERS,
  icon: 'fa-credit-card',
  permissions: [EPermissionGroup.ORDERS],
  defaultTab: {
    [ESideBarContext.COMPANY]: EOrdersState.ORDERS
  }
};

export const Reports: SideBarItem = {
  title: EDashboardPageTitle.REPORTS,
  url: EDashboardRoute.REPORTS,
  icon: 'fa-file-chart-column',
  permissions: [EPermissionGroup.FINANCIAL_REPORT],
  defaultTab: {
    [ESideBarContext.COMPANY]: EReportsTabs.FINANCIAL
  }
};

export const Subscriptions: SideBarItem = {
  title: EDashboardPageTitle.SUBSCRIPTIONS,
  url: EDashboardRoute.SUBSCRIPTIONS,
  icon: 'fa-right-left',
  permissions: [EPermissionGroup.SUBSCRIPTIONS]
};

// Project-level pages - Management
export const Analytics: SideBarItem = {
  title: EManagementPageTitle.ANALYTICS,
  url: EManagementRoute.ANALYTICS,
  icon: 'fa-regular fa-chart-simple',
  permissions: [
    EPermissionGroup.ANALYTICS,
    EPermissionGroup.SLA_PERFORMANCE_DASHBOARD
  ],
  matchAnyPermission: true,
  defaultTab: {
    [ESideBarContext.PROJECT]: EAnalyticsState.BUSINESS
  }
};

export const Segments: SideBarItem = {
  title: EManagementPageTitle.SEGMENTS,
  url: EManagementRoute.SEGMENTS,
  icon: 'fa-circle-quarter-stroke',
  permissions: [EPermissionGroup.SEGMENTS]
};

export const Pricing: SideBarItem = {
  title: EManagementPageTitle.PRICING,
  url: EManagementRoute.PRICING,
  icon: 'fa-circle-dollar',
  permissions: [EPermissionGroup.PRICING],
  defaultTab: {
    [ESideBarContext.PROJECT]: EPricingFormState.PRICING_POINTS,
    [ESideBarContext.PORTAL_LEVEL_STORE]: EPricingFormState.PRICING_POINTS
  }
};

export const Coupons: SideBarItem = {
  title: EManagementPageTitle.COUPONS,
  url: EManagementRoute.COUPONS,
  icon: 'fa-ticket-simple',
  permissions: [EPermissionGroup.COUPONS]
};

export const Localization: SideBarItem = {
  title: EManagementPageTitle.LOCALIZATION,
  url: EManagementRoute.LOCALIZATION,
  icon: 'fa-globe',
  permissions: [EPermissionGroup.OFFERS]
};

export const Settings: SideBarItem = {
  title: EManagementPageTitle.SETTINGS,
  url: EManagementRoute.SETTINGS,
  icon: 'fa-gear',
  permissions: [EPermissionGroup.COMPANY],
  defaultTab: {
    [ESideBarContext.PROJECT]: ESettingsState.DEVELOPER_SETTINGS,
    [ESideBarContext.PORTAL]: ESettingsState.DEVELOPER_SETTINGS,
    [ESideBarContext.PORTAL_LEVEL_STORE]: ESettingsState.DEVELOPER_SETTINGS
  }
};

export const PackageExplorer: SideBarItem = {
  title: ESdkPageTitle.PACKAGE_EXPLORER,
  url: ESdkRoute.PACKAGE_EXPLORER,
  icon: 'fa-globe',
  permissions: [EPermissionGroup.COMPANY]
};

export const TestAndRelease: SideBarItem = {
  title: ESdkPageTitle.TEST_AND_RELEASE,
  url: ESdkRoute.TEST_AND_RELEASE,
  icon: 'fa-wrench',
  permissions: [EPermissionGroup.COMPANY]
};

// Project-level pages - Studio
export const Builder: SideBarItem = {
  title: EStudioPageTitle.BUILDER,
  url: EStudioRoute.BUILDER,
  icon: 'fa-swatchbook',
  permissions: [EPermissionGroup.BUILDER],
  defaultTab: {
    [ESideBarContext.PROJECT]: isCheckoutUser
      ? EStoreState.CHECKOUT
      : EStoreState.GENERAL,
    [ESideBarContext.PORTAL_LEVEL_STORE]: EStoreState.GENERAL
  }
};

export const TemplatesStudio: SideBarItem = {
  title: EStudioPageTitle.TEMPLATES_STUDIO,
  url: EStudioRoute.TEMPLATES_STUDIO,
  icon: 'fa-frame',
  permissions: [EPermissionGroup.BUILDER]
};

export const Products: SideBarItem = {
  title: EStudioPageTitle.PRODUCTS,
  url: EStudioRoute.PRODUCTS,
  icon: 'fa-tag',
  permissions: [EPermissionGroup.PRODUCTS]
};

export const OffersDesign: SideBarItem = {
  title: EStudioPageTitle.OFFERS_DESIGN,
  url: EStudioRoute.OFFERS_DESIGN,
  icon: 'fa-palette',
  permissions: [EPermissionGroup.OFFER_DESIGN]
};

export const CheckoutDesign: SideBarItem = {
  title: EStudioPageTitle.CHECKOUT_DESIGN,
  url: EStudioRoute.CHECKOUT_DESIGN,
  icon: 'fa-money-check-dollar-pen',
  permissions: [EPermissionGroup.BUILDER]
};

export const Badges: SideBarItem = {
  title: EStudioPageTitle.BADGES,
  url: EStudioRoute.BADGES,
  icon: 'fa-badge-check',
  permissions: [EPermissionGroup.BADGES]
};

export const AssetsLibrary: SideBarItem = {
  title: EStudioPageTitle.ASSETS_LIBRARY,
  url: EStudioRoute.ASSETS_LIBRARY,
  icon: 'fa-image',
  permissions: [EPermissionGroup.ASSET_LIBRARY]
};

// Project-level pages - Offers
export const Bundles: SideBarItem = {
  title: EOffersPageTitle.BUNDLES,
  url: EOffersRoute.BUNDLES,
  icon: 'fa-bag-shopping',
  permissions: [EPermissionGroup.OFFERS]
};

export const Promotions: SideBarItem = {
  title: EOffersPageTitle.PROMOTIONS,
  url: EOffersRoute.PROMOTIONS,
  icon: 'fa-gallery-thumbnails',
  permissions: [EPermissionGroup.OFFERS]
};

export const Popups: SideBarItem = {
  title: EOffersPageTitle.POPUPS,
  url: EOffersRoute.POPUPS,
  icon: 'fa-gift',
  permissions: [EPermissionGroup.OFFERS]
};

export const TriggeredPopups: SideBarItem = {
  title: EOffersPageTitle.TRIGGERED_POPUPS,
  url: EOffersRoute.POPUPS,
  icon: 'fa-star',
  permissions: [EPermissionGroup.OFFERS]
};

export const DailyBonus: SideBarItem = {
  title: EOffersPageTitle.DAILY_BONUS,
  url: EOffersRoute.DAILY_BONUS,
  icon: 'fa-gift',
  permissions: [EPermissionGroup.OFFERS]
};

export const Rolling: SideBarItem = {
  title: EOffersPageTitle.ROLLING,
  url: EOffersRoute.ROLLING,
  icon: 'fa-grid-2-plus',
  permissions: [EPermissionGroup.OFFERS],
  docsLink: 'https://developers.appcharge.com/docs/rolling-offers'
};

export const BuyOneGetMany: SideBarItem = {
  title: EOffersPageTitle.BUY_ONE_GET_MANY,
  url: EOffersRoute.BUY_ONE_GET_MANY,
  icon: 'fa-grid-2-plus',
  permissions: [EPermissionGroup.OFFERS],
  docsLink: 'https://developers.appcharge.com/docs/buy-one-get-many'
};

export const CheckoutLink: SideBarItem = {
  title: EOffersPageTitle.CHECKOUT_LINKS,
  url: EOffersRoute.CHECKOUT_LINKS,
  icon: 'fa-square-up-right',
  permissions: [EPermissionGroup.OFFERS],
  docsLink: 'https://developers.appcharge.com/docs/about-checkout-link-offers'
};

export const ProgressBar: SideBarItem = {
  title: EOffersPageTitle.PROGRESS_BAR,
  url: EOffersRoute.PROGRESS_BAR,
  icon: 'fa-bars-progress',
  permissions: [EPermissionGroup.OFFERS]
};

export const Plans: SideBarItem = {
  title: EOffersPageTitle.PLANS,
  url: EOffersRoute.PLANS,
  icon: 'fa-grid-2-plus',
  permissions: [EPermissionGroup.OFFERS]
};

export const RewardCalendar: SideBarItem = {
  title: EOffersPageTitle.REWARD_CALENDAR,
  url: EOffersRoute.REWARD_CALENDAR,
  icon: 'fa-calendar-days',
  permissions: [EPermissionGroup.OFFERS]
};

// Portal-level pages
export const Pages: SideBarItem = {
  title: EPortalPageTitle.PAGES,
  url: EPortalRoute.PAGES,
  icon: 'fa-page'
};

export const Content: SideBarItem = {
  title: EPortalPageTitle.CONTENT,
  url: EPortalRoute.CONTENT,
  icon: 'fa-pen'
};

export const PortalBuilder: SideBarItem = {
  title: EPortalPageTitle.BUILDER,
  url: EPortalRoute.BUILDER,
  icon: 'fa-edit',
  defaultTab: {
    [ESideBarContext.PORTAL]: 'portal-theme'
  }
};

// Shared pages
export const Players: SideBarItem = {
  title: EManagementPageTitle.PLAYERS,
  url: EManagementRoute.PLAYERS,
  icon: 'fa-user',
  permissions: [EPermissionGroup.PLAYER],
  defaultTab: {
    [ESideBarContext.PROJECT]: EPlayersState.BLOCKED,
    [ESideBarContext.PORTAL]: EPlayersState.BLOCKED
  }
};

export const Profile: SideBarItem = {
  title: ESharedPageTitle.PROFILE,
  url: 'profile',
  icon: 'fa-user-circle'
};

// =====================
// Navigation structures using the individual page constants
// =====================

export const companyLevelNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Dashboard,
    isCollapsable: true,
    items: [Orders, Subscriptions, Reports]
  }
];

export const projectLevelNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Management,
    items: [
      Analytics,
      Players,
      Segments,
      Pricing,
      Coupons,
      Localization,
      Settings
    ],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Sdk,
    items: [PackageExplorer, TestAndRelease],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Studio,
    items: [
      Builder,
      TemplatesStudio,
      Products,
      OffersDesign,
      CheckoutDesign,
      Badges,
      AssetsLibrary
    ],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Offers,
    items: [
      Bundles,
      Promotions,
      Popups,
      Rolling,
      BuyOneGetMany,
      CheckoutLink,
      ProgressBar,
      Plans,
      RewardCalendar
    ],
    isCollapsable: true
  }
];

export const paymentLinkProjectNavItems = (
  hasSingleProject: boolean
): SideBarGroup[] => [
  {
    title: ESidebarGroupKey.Dashboard,
    items: hasSingleProject
      ? [
          Analytics,
          Orders,
          Subscriptions,
          Reports,
          Players,
          Pricing,
          Coupons,
          Settings
        ]
      : [Analytics, Players, Pricing, Coupons, Settings],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Studio,
    items: [Builder, AssetsLibrary],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Offers,
    items: [Plans],
    isCollapsable: true
  }
];

export const portalLevelNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Management,
    items: [Players, Localization, Settings],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.PortalStudio,
    items: [PortalBuilder, Pages, Content],
    isCollapsable: true
  }
];

export const portalLevelStoreNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Management,
    items: [Pricing, Settings],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Studio,
    items: [
      Builder,
      TemplatesStudio,
      Products,
      OffersDesign,
      CheckoutDesign,
      Badges,
      AssetsLibrary
    ],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Offers,
    items: [
      Bundles,
      Promotions,
      Popups,
      Rolling,
      BuyOneGetMany,
      CheckoutLink,
      ProgressBar
    ],
    isCollapsable: true
  }
];

export const pageFeatureFlagMap: Record<PageTitle | string, EFeatureFlag> = {
  [EStudioPageTitle.CHECKOUT_DESIGN]: EFeatureFlag.DASHBOARD_CHECKOUT_DESIGN,
  [EOffersPageTitle.ROLLING]: EFeatureFlag.DASHBOARD_ROLLING_OFFER_FEATURE,
  [EOffersPageTitle.BUY_ONE_GET_MANY]:
    EFeatureFlag.DASHBOARD_ROLLING_OFFER_1N_ENABLED,
  [EManagementPageTitle.COUPONS]: EFeatureFlag.DASHBOARD_COUPONS,
  [ESdkPageTitle.PACKAGE_EXPLORER]: EFeatureFlag.DASHBOARD_SDK_AUTO_UPDATER_UI,
  [ESdkPageTitle.TEST_AND_RELEASE]: EFeatureFlag.DASHBOARD_SDK_AUTO_UPDATER_UI,
  [EDashboardPageTitle.SUBSCRIPTIONS]: EFeatureFlag.DASHBOARD_SUBSCRIPTIONS,
  [EOffersPageTitle.PLANS]: EFeatureFlag.DASHBOARD_SUBSCRIPTIONS,
  [EStudioPageTitle.TEMPLATES_STUDIO]: EFeatureFlag.DASHBOARD_TEMPLATES_STUDIO,
  [EOffersPageTitle.DAILY_BONUS]: EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU,
  [EOffersPageTitle.REWARD_CALENDAR]: EFeatureFlag.DASHBOARD_REWARD_CALENDAR
};

export const superAdminAllowedPages: PageTitle[] = [
  EStudioPageTitle.TEMPLATES_STUDIO
];

export const projectTypeRestrictions = {
  independentCheckout: {
    disabledItems: {
      [ESidebarGroupKey.Management]: [Segments],
      [ESidebarGroupKey.Studio]: [
        Products,
        OffersDesign,
        CheckoutDesign,
        AssetsLibrary,
        Badges,
        TemplatesStudio
      ],
      [ESidebarGroupKey.Offers]: [
        Bundles,
        Promotions,
        Popups,
        TriggeredPopups,
        DailyBonus,
        Rolling,
        BuyOneGetMany,
        CheckoutLink
      ]
    } as Record<ESidebarGroupKey, SideBarItem[]>
  }
};

export const sidebarChipConfig: Record<ESideBarChipType, ESideBarChipConfig> = {
  coming_soon: {
    styles:
      'border-zinc-100 bg-zinc-100 text-zinc-500 group-hover/sidebar-menu-btn:border-violet-50 group-hover/sidebar-menu-btn:bg-violet-50 group-hover/sidebar-menu-btn:text-indigo-950 group-focus/sidebar-menu-btn:border-violet-50 group-focus/sidebar-menu-btn:bg-violet-50 group-focus/sidebar-menu-btn:text-indigo-950',
    disabledStateStyles: 'border-violet-50 bg-violet-50 text-indigo-950',
    title: 'Coming Soon',
    tooltip: 'Contact your account manager for early access and more details'
  },
  new: {
    styles: 'border-indigo-400 bg-indigo-400 text-base-white',
    title: 'New'
  },
  locked: {
    styles: 'border-zinc-700 bg-zinc-700 text-zinc-200',
    title: 'Locked',
    tooltip:
      'This feature is currently locked. Contact your Appcharge representative to learn more or request access.'
  },
  beta: {
    styles:
      'border-transparent bg-[rgba(14,165,233,0.20)] text-blue-950 px-2 py-1.5',
    title: 'Beta',
    tooltip: "This feature is in beta. We'd love your feedback!"
  }
};
